import _ from 'lodash'
import { AxiosResponse } from 'axios'
import { all, call, put, takeLatest } from 'redux-saga/effects'
import { getType } from 'typesafe-actions'

import { apiCall } from '../../../utils/api'
import { TableActions } from '../../table'
import { withLoading } from '../../loading'

import {
  Notification,
  NotificationTemplate,
  NotificationsStats,
  OrderPartiallyPlaced,
  ErrorDuringOrderProcessing,
  ReportIsDelivered,
  CancelledReportRequest,
  ErrorDuringReportDownload,
  PriceChangeInYourOrder
} from './types'
import { AccountNotificationsActions } from './actions'

type NotificationsResponse = {
  notifications: Notification[]
  meta: {
    totalCount: number
    pageCount: number
    pageNumber: number
  }
}

type NotificationTemplatesResponse = {
  data: NotificationTemplate[]
  error: any
}

type ProductsResponse = {
  data: {
    bifCode: string
    name: string
  }[]
}

type FetchNotificationsType = ReturnType<typeof AccountNotificationsActions.fetchNotifications>

export function* loadNotifications(action: FetchNotificationsType) {
  const { data: notificationsData }: AxiosResponse<NotificationsResponse> = yield call(apiCall, {
    url: `/api/notifications`,
    method: 'get',
    params: {
      page: action.payload.page,
      pageSize: action.payload.pageSize
    }
  })

  type NotificationWithCustomerRequests =
    | OrderPartiallyPlaced
    | ErrorDuringOrderProcessing
    | ReportIsDelivered
    | CancelledReportRequest
    | ErrorDuringReportDownload
    | PriceChangeInYourOrder

  const hasCustomerRequests = (notification: Notification): notification is NotificationWithCustomerRequests =>
    (notification as NotificationWithCustomerRequests).customerRequests !== undefined

  const productCodes = _.uniq(
    _.flatMap(notificationsData.notifications, n =>
      hasCustomerRequests(n) ? _.map(n.customerRequests, cr => cr.reportCode) : []
    )
  )

  let productsData: ProductsResponse
  if (productCodes.length > 0) {
    const { data }: AxiosResponse<ProductsResponse> = yield call(apiCall, {
      url: '/api/products/products',
      method: 'post',
      data: { productCodes },
      umbracoCall: true
    })
    productsData = data
  }

  const localizeReportNamesFromCms = (notification: NotificationWithCustomerRequests): Notification => {
    notification.customerRequests = _.map(notification.customerRequests, cr => ({
      ...cr,
      reportName: _.find(productsData?.data, pd => pd.bifCode === cr.reportCode)?.name ?? cr.reportName
    }))
    return notification
  }

  yield put(
    AccountNotificationsActions.setNotifications({
      page: notificationsData.meta.pageNumber,
      totalPages: notificationsData.meta.pageCount,
      data: _.map(notificationsData.notifications, n => (hasCustomerRequests(n) ? localizeReportNamesFromCms(n) : n))
    })
  )

  if (notificationsData.meta.pageNumber === 1) {
    yield put(
      TableActions.setPagination({
        tableKey: 'archiveNotifications',
        pagination: {
          current: notificationsData.meta.pageNumber,
          pageSize: action.payload.pageSize,
          total: notificationsData.meta.totalCount
        }
      })
    )
  }
}

export function* loadNotificationTemplates() {
  const { data }: AxiosResponse<NotificationTemplatesResponse> = yield call(apiCall, {
    umbracoCall: true,
    url: `/api/notification-templates`,
    method: 'get'
  })

  yield put(
    AccountNotificationsActions.setNotificationTemplates({
      data: data.data
    })
  )
}

export function* loadOnPageChange({ payload }: ReturnType<typeof TableActions.setPagination>) {
  if (payload.tableKey !== 'archiveNotifications') return

  const { pagination } = payload

  yield put(
    AccountNotificationsActions.fetchNotifications({
      page: pagination.current,
      pageSize: pagination.pageSize
    })
  )
}

export function* loadNotificationStats() {
  const { data }: AxiosResponse<NotificationsStats> = yield call(apiCall, {
    url: `/api/notifications/stats`,
    method: 'get'
  })

  yield put(AccountNotificationsActions.setNotificationsStats(data))
}

export function* markAllAsRead() {
  yield call(apiCall, {
    url: `/api/notifications/mark-all-read`,
    method: 'post'
  })
  yield put(AccountNotificationsActions.fetchNotificationsStats())
}

export function* accountNotificationsSagas() {
  yield all([
    takeLatest(
      getType(AccountNotificationsActions.fetchNotifications),
      withLoading('accountNotifications', loadNotifications)
    ),
    takeLatest(getType(TableActions.changePagination), loadOnPageChange),
    takeLatest(
      getType(AccountNotificationsActions.fetchNotificationTemplates),
      withLoading('accountNotificationTemplates', loadNotificationTemplates)
    ),
    takeLatest(getType(AccountNotificationsActions.fetchNotificationsStats), loadNotificationStats),
    takeLatest(getType(AccountNotificationsActions.markAllNotificationsAsRead), markAllAsRead)
  ])
}
